import { UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts';

const rawMode = (
  import.meta.env.VITE_PLATFORM_ENV ||
  (import.meta.env.VITE_DEMO_MODE === '1' ? 'demo' : 'staging')
)
  .toString()
  .toLowerCase();

export const isDemoMode = rawMode === 'demo';

const getBaseUrl = (): string => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/$/, '');
  }

  const isDev = import.meta.env.DEV;
  return (isDev ? 'http://localhost:5173' : 'https://app.rugira.ch').replace(/\/$/, '');
};

const DEFAULT_KEYCLOAK_REALM = 'http://localhost:8080/auth/realms/rugirinka';
const DEFAULT_CLIENT_ID = 'rugirinka-web-client';

const trimmed = (value?: string | null): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const next = value.trim();
  return next.length > 0 ? next : undefined;
};

const rawAuthority = trimmed(import.meta.env.VITE_AUTH_BASE_URL) || DEFAULT_KEYCLOAK_REALM;
const rawClientId = trimmed(import.meta.env.VITE_AUTH_CLIENT_ID) || DEFAULT_CLIENT_ID;
const redirectUri =
  trimmed(import.meta.env.VITE_AUTH_REDIRECT_URI) || `${getBaseUrl()}/auth/callback`;
const silentRedirectUri =
  trimmed(import.meta.env.VITE_AUTH_SILENT_REDIRECT_URI) || `${getBaseUrl()}/auth/silent-callback`;
const logoutRedirectUri =
  trimmed(import.meta.env.VITE_AUTH_LOGOUT_REDIRECT_URI) || `${getBaseUrl()}/login`;
const configuredScope =
  trimmed(import.meta.env.VITE_AUTH_SCOPE) || 'openid profile email roles';

const userStore =
  typeof window !== 'undefined'
    ? new WebStorageStateStore({ store: window.sessionStorage })
    : undefined;

export const oidcAuthority = rawAuthority;
export const oidcClientId = rawClientId;
export const oidcScope = configuredScope;

export const oidcConfig: UserManagerSettings = {
  authority: oidcAuthority,
  client_id: oidcClientId,
  redirect_uri: redirectUri,
  post_logout_redirect_uri: logoutRedirectUri,
  response_type: 'code',
  scope: oidcScope,
  loadUserInfo: true,
  automaticSilentRenew: true,
  silent_redirect_uri: silentRedirectUri,
  userStore,
};

export interface OidcUserProfile extends Record<string, unknown> {
  sub: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  preferred_username?: string;
  email?: string;
  picture?: string;
  tenant_id?: string;
  roles?: string[];
  realm_access?: {
    roles?: string[];
  };
  resource_access?: Record<string, { roles?: string[] }>;
  tenant_roles?: Record<string, string[]> | string[];
  tenant_roles_map?: Record<string, string[]>;
  tenant_uuid_map?: Record<string, string>;
  tenant_uuid?: string;
  current_tenant_uuid?: string;
  support_impersonation?: Record<string, unknown>;
  compliance_impersonation?: Record<string, unknown>;
}

export interface ExtendedUser {
  id: string;
  email: string;
  name: string;
  profile: {
    name: string;
    email: string;
    given_name?: string;
    family_name?: string;
    picture?: string;
  };
  access_token: string;
  refresh_token?: string;
  expired: boolean;
  tenant_roles?: Record<string, string[]>;
  current_tenant?: string;
  tenant_id?: string;
  tenant_uuid_map?: Record<string, string>;
  tenant_uuid?: string | null;
  current_tenant_uuid?: string | null;
  roles?: string[];
}
